import { dehydrate, QueryClient } from '@tanstack/react-query'
import { serverSideTrackEvent } from '@twlvxtwlv/analytics/browser'
import { _trackEventOnServerWithLogging } from '@twlvxtwlv/analytics/server'
import { getBackgroundImageUrl } from '@twlvxtwlv/cms'
import { extractRequestUrl, signUp } from '@twlvxtwlv/tokenforge'
import type { SignUpErrorType, SignUpInput } from '@twlvxtwlv/types'
import { SignUp } from '@twlvxtwlv/ui/src/components/pages/marketplace/sign-up'
import { TaskContainer } from '@twlvxtwlv/ui/src/components/shared/task-container'
import { extractRegistrationOriginUrl } from '@twlvxtwlv/utils/routing'
import { _initDistinctId, initDistinctId } from '@twlvxtwlv/utils/src/cookie'
import { deleteCookie } from 'cookies-next'
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import { getServerSession } from 'next-auth'
import { signOut } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { authOptions } from '../api/auth/[...nextauth]'
import { getUseClaimOptions, getUseProductOptions, useClaim, useProduct } from '@twlvxtwlv/hooks'
import { getUseCampaignOptions, useCampaign } from '@twlvxtwlv/hooks/src/cms/use-campaign'

interface SignUpPageProps {
  campaignId: string | null
  claimId: string | null
  locale: string
  origin: string | null
  productId: string | null
  rawParams: string | null
  redirectTo: string | null
}

export default function SignUpPage({
  campaignId,
  claimId,
  locale,
  origin,
  productId,
  rawParams,
  redirectTo,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation(['user-management', 'migration', 'common', 'claim'])
  const router = useRouter()
  const [error, setError] = useState<SignUpErrorType | undefined>(undefined)

  const distinctId = initDistinctId()

  const { product } = useProduct({
    id: productId!,
    locale,
  })

  const { claimAttributes } = useClaim({
    locale,
    claimId,
  })

  const { campaignAttributes } = useCampaign({
    locale,
    id: campaignId,
    currentNftsPage: 1,
    currentNftsPageSize: 1,
  })

  async function handleOnSubmit(signUpInput: SignUpInput) {
    // clean up old cookies before sign up
    deleteCookie('LR_COOKIE')

    const registrationOrigin = extractRegistrationOriginUrl({
      campaignId,
      claimId,
      locale,
      productId,
      redirectTo,
    })

    const response = await signUp(signUpInput, registrationOrigin)
    if (response.status === 204) {
      // Success

      serverSideTrackEvent(
        { userId: distinctId },
        {
          eventName: 'sign-up-marketplace-started',
          redirectUrl: registrationOrigin,
        }
      )

      await signOut({ redirect: false })

      const redirectUrl = rawParams
        ? `/sign-up/success?${decodeURIComponent(rawParams)}`
        : `/sign-up/success`
      await router.push(redirectUrl)
    } else if (response.status === 422) {
      setError('sign-up-error-email-already-in-use')
    } else {
      setError('sign-up-error-general')
    }
  }

  function handleOnGotoLogin() {
    const redirectUrl = rawParams ? `/login?${decodeURIComponent(rawParams)}` : `/login`
    router.push(redirectUrl)
  }

  return (
    <>
      <Head>
        <title>{t('common:meta-title-sign-up')}</title>
        <meta name="description" content={t('common:meta-description-sign-up') ?? undefined} />
      </Head>
      <TaskContainer
        bgImage={getBackgroundImageUrl(
          claimAttributes?.image?.data?.attributes?.url ??
            campaignAttributes?.Background?.data?.attributes?.url
        )}
        bgRepeat={false}
      >
        <SignUp
          error={error}
          onGotoLogin={handleOnGotoLogin}
          onSubmit={handleOnSubmit}
          origin={origin}
          product={product}
        />
      </TaskContainer>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<SignUpPageProps> = async ({
  locale = 'de',
  req,
  res,
  resolvedUrl,
}: GetServerSidePropsContext) => {
  const session = await getServerSession(req, res, authOptions)

  const distinctId = _initDistinctId({ req, res })

  const userAgent = req.headers['user-agent'] as string

  _trackEventOnServerWithLogging(
    { userId: distinctId },
    { eventName: 'sign-up-page-visited' },
    userAgent
  )

  if (session) {
    return {
      redirect: {
        destination: `/${locale}/gallery`,
        statusCode: 307,
      },
    }
  }

  const queryClient = new QueryClient()

  const { redirectTo, origin, productId, claimId, campaignId, rawParams } =
    extractRequestUrl(resolvedUrl)

  const requests = []
  if (productId) {
    const productRequest = queryClient.prefetchQuery(
      getUseProductOptions({
        id: productId,
        locale,
      })
    )

    requests.push(productRequest)
  }

  if (claimId) {
    const claimRequest = queryClient.prefetchQuery(
      getUseClaimOptions({
        claimId,
        locale,
      })
    )

    requests.push(claimRequest)
  }

  if (campaignId) {
    const campaignRequest = queryClient.prefetchQuery(
      getUseCampaignOptions({
        locale,
        id: campaignId,
        currentNftsPage: 1,
        currentNftsPageSize: 1,
      })
    )

    requests.push(campaignRequest)
  }

  await Promise.allSettled(requests)

  return {
    props: {
      campaignId,
      claimId,
      dehydratedState: dehydrate(queryClient),
      locale,
      origin,
      productId,
      rawParams,
      redirectTo,
      ...(await serverSideTranslations(locale, [
        'checkout',
        'claim',
        'common',
        'migration',
        'user-management',
      ])),
    },
  }
}
